import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const DEST = { super_admin: "/platform", owner: "/owner", staff: "/staff" };

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      nav(DEST[data.user.role] || "/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/33673934/pexels-photo-33673934.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C]/30 via-transparent to-[#0A0A0C]" />
        <div className="absolute bottom-10 left-10 right-10">
          <Link to="/" className="font-display text-3xl gold-text inline-flex items-center gap-2"><Logo size={28} />Mnu<span className="text-white/90">Verse</span></Link>
          <p className="mt-2 font-display text-4xl max-w-md leading-tight">Operator console — engineered for service.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Operator Login</div>
          <h1 className="font-display text-5xl mt-2">Sign in</h1>
          <p className="text-sm text-white/60 mt-2">Owner · Staff</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <Field icon={<Mail className="w-4 h-4" />} placeholder="you@restaurant.com" value={email} onChange={setEmail} type="email" testid="admin-email-input" />
            <Field icon={<Lock className="w-4 h-4" />} placeholder="password" value={password} onChange={setPassword} type="password" testid="admin-password-input" />
            <button disabled={loading} data-testid="admin-login-btn" className="gold-btn rounded-full w-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? "Signing in…" : (<>Enter <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          {/* <div className="mt-8 p-4 rounded-xl border border-white/10 bg-[#121418]/60">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50 mb-3">Demo accounts</div>
            <div className="space-y-2 text-sm">
              <QuickBtn onClick={() => quick("superadmin@crave.os", "admin123")} label="Super Admin" sub="superadmin@crave.os" testid="quick-super-admin" />
              <QuickBtn onClick={() => quick("owner@gildedfork.com", "owner123")} label="Restaurant Owner" sub="owner@gildedfork.com" testid="quick-owner" />
              <QuickBtn onClick={() => quick("staff@gildedfork.com", "staff123")} label="Staff" sub="staff@gildedfork.com" testid="quick-staff" />
            </div>
          </div> */}

          {/* <div className="mt-6 text-xs text-white/40">
            Guest? <Link to="/login/customer" className="gold-text hover:underline">Use OTP login</Link>
          </div> */}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ icon, placeholder, value, onChange, type = "text", testid }) {
  return (
    <div className="relative">
      {icon && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">{icon}</div>}
      <input
        data-testid={testid}
        value={value}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-[#121418] border border-white/10 rounded-xl py-3 text-white placeholder-white/30 focus:border-[#E5A93C] focus:ring-1 focus:ring-[#E5A93C] transition ${icon ? "pl-10 pr-4" : "px-4"}`}
      />
    </div>
  );
}

function QuickBtn({ onClick, label, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="w-full text-left flex items-center justify-between p-2.5 rounded-lg hover:bg-white/5 transition-colors">
      <div>
        <div className="text-white">{label}</div>
        <div className="text-xs text-white/50 font-mono">{sub}</div>
      </div>
      <span className="text-xs text-[#E5A93C]">Use →</span>
    </button>
  );
}
